import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Download } from 'lucide-react';
import { ShareCard } from './ShareCard';
interface FeedbackShareOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}
const emojiOptions = [{
  emoji: '😊',
  label: 'Helpful'
}, {
  emoji: '😌',
  label: 'Peaceful'
}, {
  emoji: '🙏',
  label: 'Grateful'
}, {
  emoji: '💙',
  label: 'Encouraged'
}, {
  emoji: '✨',
  label: 'Inspired'
}];
export function FeedbackShareOverlay({
  isOpen,
  onClose
}: FeedbackShareOverlayProps) {
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [showShareCard, setShowShareCard] = useState(false);
  return <AnimatePresence data-id="element-81">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" data-id="element-82" />

          {/* Overlay */}
          <motion.div initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 50
      }} className="fixed inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl z-50 max-h-[90vh] overflow-y-auto" data-id="element-83">
            <div className="p-6" data-id="element-84">
              {/* Header */}
              <div className="flex items-center justify-between mb-6" data-id="element-85">
                <h3 className="text-xl font-bold text-slate-800" data-id="element-86">
                  {showShareCard ? 'Share Your Encouragement' : 'How did this help you?'}
                </h3>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400" aria-label="Close" data-id="element-87">
                  <X className="w-5 h-5 text-slate-600" data-id="element-88" />
                </button>
              </div>

              {!showShareCard ? <>
                  {/* Emoji Feedback */}
                  <div className="mb-6" data-id="element-89">
                    <p className="text-sm text-slate-600 mb-3" data-id="element-90">
                      Select how you feel:
                    </p>
                    <div className="flex gap-3 justify-center" data-id="element-91">
                      {emojiOptions.map(option => <button key={option.emoji} onClick={() => setSelectedEmoji(option.emoji)} className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-sky-400 ${selectedEmoji === option.emoji ? 'bg-sky-100 scale-110' : 'hover:bg-slate-50'}`} data-id="element-92">
                          <span className="text-3xl" data-id="element-93">{option.emoji}</span>
                          <span className="text-xs text-slate-600" data-id="element-94">
                            {option.label}
                          </span>
                        </button>)}
                    </div>
                  </div>

                  {/* Text Feedback */}
                  <div className="mb-6" data-id="element-95">
                    <label className="block text-sm text-slate-600 mb-2" data-id="element-96">
                      Share more (optional):
                    </label>
                    <textarea value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Tell us how this helped you..." className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/20 resize-none text-slate-800 placeholder:text-slate-400 min-h-[100px]" data-id="element-97" />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3" data-id="element-98">
                    <button onClick={() => setShowShareCard(true)} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-sky-400 text-sky-600 font-medium rounded-xl hover:bg-sky-50 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-99">
                      <Share2 className="w-4 h-4" data-id="element-100" />
                      <span data-id="element-101">Share</span>
                    </button>
                    <button onClick={onClose} className="flex-1 px-6 py-3 bg-gradient-to-r from-sky-400 to-purple-400 text-white font-medium rounded-xl hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-purple-400" data-id="element-102">
                      Submit Feedback
                    </button>
                  </div>
                </> : <>
                  {/* Share Card Preview */}
                  <div className="mb-6" data-id="element-103">
                    <ShareCard verse="Cast all your anxiety on him because he cares for you." reference="1 Peter 5:7 (NIV)" prayer="Heavenly Father, I thank You that You invite me to cast all my cares upon You..." data-id="element-104" />
                  </div>

                  {/* Share Actions */}
                  <div className="space-y-3" data-id="element-105">
                    <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-sky-400 to-purple-400 text-white font-medium rounded-xl hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-purple-400" data-id="element-106">
                      <Download className="w-4 h-4" data-id="element-107" />
                      <span data-id="element-108">Download Image</span>
                    </button>
                    <button className="w-full px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 font-medium rounded-xl hover:border-sky-300 hover:bg-sky-50 transition-all focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-109">
                      Share to WhatsApp
                    </button>
                    <button className="w-full px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 font-medium rounded-xl hover:border-sky-300 hover:bg-sky-50 transition-all focus:outline-none focus:ring-2 focus:ring-sky-400" data-id="element-110">
                      Share to Instagram
                    </button>
                  </div>
                </>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}